/**
 * The forum boards. Each board owns its own list URL (/forum/<slug>) and states once
 * who may read and post there; threads record which board they live on, not who may
 * read them. Adding a board is an entry here plus a matching route file.
 *
 * `outsiders` says what a viewer without access gets at the board's URL: "hide" is a
 * 404, so the board's existence stays private; "subscribe" shows a locked page that
 * invites them to subscribe.
 *
 * Client-safe: the composer's validation schema and the tab strip both need this, so
 * it must not pull in server-only code. Access resolution lives in forum.server.ts.
 */

export type ForumAccess = {
  isAdmin: boolean
  isCreator: boolean
  isPaidMember: boolean
}

export const FORUM_BOARDS = {
  stars: {
    label: "MakeLoveNotPornstars",
    title: "MakeLoveNotPornstar Forum",
    audience: "MLNP Stars",
    outsiders: "hide",
    canAccess: (access: ForumAccess) => access.isCreator,
  },
  members: {
    label: "Members",
    title: "MakeLoveNotPorn Members Forum",
    audience: "members",
    outsiders: "subscribe",
    canAccess: (access: ForumAccess) => access.isCreator || access.isPaidMember,
  },
} as const

export type ForumBoardSlug = keyof typeof FORUM_BOARDS

export const FORUM_BOARD_SLUGS = Object.keys(FORUM_BOARDS) as [ForumBoardSlug, ...ForumBoardSlug[]]

export const isBoardSlug = (value: unknown): value is ForumBoardSlug =>
  typeof value === "string" && value in FORUM_BOARDS

/** Admins reach every board, so no board predicate needs an admin clause of its own. */
export const canAccessBoard = (access: ForumAccess, slug: ForumBoardSlug) =>
  access.isAdmin || FORUM_BOARDS[slug].canAccess(access)

export const accessibleBoards = (access: ForumAccess): ForumBoardSlug[] =>
  FORUM_BOARD_SLUGS.filter((slug) => canAccessBoard(access, slug))

/**
 * Where the forum link (the /forum entry and the user menu) sends a viewer: the first
 * board they can reach, else the members board, which invites them to subscribe.
 */
export const primaryBoard = (access?: ForumAccess): ForumBoardSlug => (access && accessibleBoards(access)[0]) || "members"

/** Falls back to the raw slug for a thread on a board no longer in the registry. */
export const boardLabel = (slug: string | null | undefined) =>
  isBoardSlug(slug) ? FORUM_BOARDS[slug].label : (slug ?? "")

export const boardTabs = (access: ForumAccess) =>
  accessibleBoards(access).map((slug) => ({
    slug,
    href: `/forum/${slug}`,
    label: FORUM_BOARDS[slug].label,
  }))

type ReplyID = string | bigint | number | null

/** IDs reach the page as JSON strings; compare as strings so either side may still be a bigint. */
export const isThreadAuthor = (replyUserID: ReplyID, authorID: ReplyID) =>
  replyUserID != null && authorID != null && String(replyUserID) === String(authorID)

type ThreadReply = {
  UserID: ReplyID
  Avatar: string
  DisplayName: string
  IsAnonymous: boolean
  Deleted: number
  CreatedAt: string | Date
  Children: ThreadReply[]
}

/**
 * Who is in a thread, how many replies it shows, and when it last moved, read from the replies the page already
 * loaded. Anonymous and deleted replies add no participant, so neither reveals its author.
 */
export const threadActivity = (replies: ThreadReply[]) => {
  const all: ThreadReply[] = []
  const walk = (nodes: ThreadReply[]) => nodes.forEach((node) => (all.push(node), walk(node.Children)))
  walk(replies)

  const participants = new Map<string, ThreadReply>()
  all.forEach((reply) => {
    if (reply.UserID != null && !reply.IsAnonymous && !reply.Deleted && !participants.has(String(reply.UserID))) {
      participants.set(String(reply.UserID), reply)
    }
  })

  const lastReplyAt = all.reduce<string | null>((latest, { CreatedAt }) => {
    const at = new Date(CreatedAt).toISOString()
    return !latest || at > latest ? at : latest
  }, null)

  return { participants: [...participants.values()], lastReplyAt, count: all.length }
}
